import L from 'leaflet'

/**
 * LayerManager - Manages geometry layers for the map
 *
 * Layers are containers for map items (markers, shapes, etc.) with independent
 * z-index control. Each layer uses a custom Leaflet pane for proper stacking order.
 *
 * Common layers: 'units', 'shapes', 'myself'
 */
export default class LayerManager {
  /**
   * Create a new LayerManager
   * @param {L.Map} leafletMap - The Leaflet map instance
   * @param {Function} [warnFn] - Optional warning function for logging
   */
  constructor(leafletMap, warnFn = console.warn) {
    this._map = leafletMap
    this._layers = {}
    this._warn = warnFn
  }

  /**
   * Create a new geometry layer
   *
   * @param {string} id - Unique layer identifier (e.g., 'units', 'shapes')
   * @param {Object} [config={}] - Layer configuration
   * @param {number} [config.zindex=0] - Z-index for rendering order (higher = on top)
   * @param {boolean} [config.toggleable=true] - Can user toggle visibility?
   * @param {boolean} [config.visible=true] - Initially visible?
   */
  setLayer(id, config = {}) {
    if (this._layers[id]) {
      this._warn('Layer already exists:', id)
      return
    }

    const layerConfig = {
      id,
      zindex: config.zindex || 0,
      toggleable: config.toggleable !== false, // default true
      visible: config.visible !== false, // default true
    }

    // Create Leaflet LayerGroup with proper z-index
    const layerGroup = L.layerGroup()

    // Set pane for z-index control
    const paneId = `layer-${id}`
    if (!this._map || !this._map.getPane(paneId)) {
      const pane = this._map.createPane(paneId)
      pane.style.zIndex = layerConfig.zindex
    }

    layerGroup.options.pane = paneId

    // Add to map if visible
    if (layerConfig.visible) {
      layerGroup.addTo(this._map)
    }

    // Store layer
    this._layers[id] = {
      config: layerConfig,
      group: layerGroup,
      items: {}, // will store items in this layer
    }
  }

  /**
   * Remove a layer and all its items
   *
   * @param {string} id - Layer identifier to remove
   */
  removeLayer(id) {
    const layer = this._layers[id]
    if (!layer) return

    // Remove from map
    this._map.removeLayer(layer.group)

    // Remove pane
    const paneId = `layer-${id}`
    const pane = this._map.getPane(paneId)
    if (pane && pane.parentNode) {
      pane.parentNode.removeChild(pane)
    }

    delete this._layers[id]
  }

  /**
   * Toggle layer visibility
   *
   * @param {string} id - Layer identifier
   * @param {boolean} visible - True to show, false to hide
   */
  setLayerVisibility(id, visible) {
    const layer = this._layers[id]
    if (!layer) {
      this._warn('Layer not found:', id)
      return
    }

    // Check if toggleable
    if (!layer.config.toggleable && !visible) {
      this._warn('Layer is not toggleable:', id)
      return
    }

    layer.config.visible = visible

    if (visible) {
      layer.group.addTo(this._map)
    } else {
      this._map.removeLayer(layer.group)
    }
  }

  /**
   * Get layer configuration by ID
   *
   * @param {string} id - Layer identifier
   * @returns {Object|undefined} Layer configuration or undefined if not found
   */
  getLayer(id) {
    return this._layers[id]?.config
  }

  /**
   * Get all layer configurations
   *
   * @returns {Array<Object>} Array of all layer configurations
   */
  getLayers() {
    return Object.keys(this._layers).map(id => this._layers[id].config)
  }

  /**
   * Check if a layer exists
   *
   * @param {string} id - Layer identifier
   * @returns {boolean} True if layer exists
   */
  hasLayer(id) {
    return !!this._layers[id]
  }

  /**
   * Get the internal layer object (for ItemManager use)
   *
   * @param {string} id - Layer identifier
   * @returns {Object|undefined} Internal layer object with group and items
   * @private
   */
  _getLayer(id) {
    return this._layers[id]
  }

  /**
   * Get the Leaflet LayerGroup for a layer
   *
   * @param {string} id - Layer identifier
   * @returns {L.LayerGroup|undefined} Leaflet LayerGroup
   */
  getLayerGroup(id) {
    return this._layers[id]?.group
  }

  /**
   * Get all layers internal storage (for cleanup)
   *
   * @returns {Object} All layers
   * @private
   */
  _getAllLayers() {
    return this._layers
  }
}
